import React, { FC, useRef, useState } from 'react';

import { Icon } from '@iconify/react';
import { Popper, Typography } from '@mui/material';

import IconButton from '~/components/atoms/buttons/IconButton';
import useClickOutside from '~/hooks/useClickOutside';
import { BenefitItemType } from '~/modules/feature-access/policy';
import { Typographies } from '~/theme/typography';

import styles from './styles.module.scss';

type Props = {
  item: BenefitItemType;
};

const BenefitItem: FC<Props> = ({ item }) => {
  const [isPreviewOpened, setIsPreviewOpened] = useState(false);
  const popperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useClickOutside({
    refs: [popperRef],
    callback: () => setIsPreviewOpened(false),
  });

  const { text, PreviewComponent } = item;

  return (
    <div className={styles.item}>
      <Icon
        icon="material-symbols:check-rounded"
        className={styles.item__icon}
      />

      <Typography
        variant={Typographies.BODY_MEDIUM}
        component="div"
        className={styles.item__text}
      >
        {text}
      </Typography>

      {PreviewComponent && (
        <>
          <button
            ref={anchorRef}
            className={styles.item__preview_button}
            onClick={() => setIsPreviewOpened(true)}
          >
            <Icon icon="material-symbols:info-outline-rounded" />
          </button>

          <Popper
            ref={popperRef}
            sx={{ zIndex: 1400 }}
            open={isPreviewOpened}
            anchorEl={anchorRef.current}
            placement="right"
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
              {
                name: 'flip',
                enabled: true,
                options: {
                  fallbackPlacements: ['left', 'bottom', 'top'],
                },
              },
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  boundary: 'viewport',
                },
              },
            ]}
          >
            <div className={styles.item__preview_popper}>
              <IconButton
                onClick={() => setIsPreviewOpened(false)}
                size="extraSmall"
                className={styles.item__preview_popper_button}
              >
                <Icon icon="material-symbols:close-rounded" width="24" />
              </IconButton>

              <PreviewComponent />
            </div>
          </Popper>
        </>
      )}
    </div>
  );
};

export default BenefitItem;
